import React from 'react';
import { CheckCircle2, XCircle, AlertCircle, Edit3, Trash2, CreditCard, Power } from 'lucide-react';
import { motion } from 'framer-motion';

interface TerminalCardProps {
  terminal: any;
  onEdit: (terminal: any, jumpToActivation?: boolean) => void;
  onDelete: (id: string) => void;
  index: number;
}

export const TerminalCard: React.FC<TerminalCardProps> = ({ terminal, onEdit, onDelete, index }) => {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(terminal.id);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(terminal, false);
  };

  const handleActivate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(terminal, true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className="group relative p-4 hover:bg-gradient-to-l hover:from-blue-50/50 transition-all cursor-pointer"
      onClick={() => onEdit(terminal)}
    >
      <div className="flex items-start gap-4">
        <motion.div
          whileHover={{ rotate: [0, -5, 5, 0] }}
          transition={{ duration: 0.5 }}
          className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
            terminal.is_active
              ? 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-200'
              : 'bg-gray-200'
          }`}
        >
          <CreditCard className={`h-6 w-6 ${terminal.is_active ? 'text-white' : 'text-gray-500'}`} />
        </motion.div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-[17px] font-bold text-gray-900 truncate">
              {terminal.terminal_name || 'מסוף ללא שם'}
            </h3>
            {terminal.is_active ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 500 }}
              >
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
              </motion.div>
            ) : (
              <XCircle className="h-5 w-5 text-gray-400 flex-shrink-0" />
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            {terminal.enable_invoice && (
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 text-[12px] font-semibold rounded-lg border border-blue-200"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                חשבוניות
              </motion.span>
            )}
            {terminal.sandbox_mode && (
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.15 }}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-50 to-amber-100 text-amber-700 text-[12px] font-semibold rounded-lg border border-amber-200"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                טסט
              </motion.span>
            )}
            {!terminal.is_active && (
              <motion.span
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 text-[12px] font-medium rounded-lg"
              >
                לא פעיל
              </motion.span>
            )}
          </div>

          <div className="space-y-1 text-[14px]">
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-24">מספר מסוף:</span>
              <span className="text-gray-900 font-medium">{terminal.pelecard_terminal_number || '-'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-24">מספר חנות:</span>
              <span className="text-gray-900 font-medium">{terminal.pelecard_shop_number || '-'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-24">משתמש:</span>
              <span className="text-gray-900 font-medium">{terminal.pelecard_user || '-'}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {!terminal.is_active ? (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleActivate}
              className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold text-[14px] shadow-lg shadow-green-200 flex items-center gap-2"
              aria-label="הפעל מסוף"
            >
              <Power className="h-4 w-4" />
              הפעל
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={handleEdit}
              className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
              aria-label="ערוך מסוף"
            >
              <Edit3 className="h-5 w-5" />
            </motion.button>
          )}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleDelete}
            className="p-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            aria-label="מחק מסוף"
          >
            <Trash2 className="h-5 w-5" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};
